import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../utils/api';

interface User {
    id: string;
    email: string;
    name: string;
    role: string;
    organizationId?: string;
    avatar?: string;
    subscription?: {
        plan: string;
        status: string;
    };
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    expiresAt: number | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    hasCompletedOnboarding: boolean;

    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, name: string) => Promise<void>;
    logout: () => void;
    refreshAccessToken: () => Promise<boolean>;
    clearError: () => void;
    setOnboardingComplete: () => void;
    skipOnboarding: () => void;
    setAuth: (data: { user: User; accessToken: string; refreshToken: string; expiresIn: number; isNewUser?: boolean }) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            hasCompletedOnboarding: false,

            login: async (email: string, password: string) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await api.post('/auth/login', { email, password });
                    const { user, accessToken, refreshToken, expiresIn, hasCompletedOnboarding } = response.data;

                    const expiresAt = Date.now() + (expiresIn * 1000);

                    set({
                        user,
                        accessToken,
                        refreshToken,
                        expiresAt,
                        isAuthenticated: true,
                        isLoading: false,
                        hasCompletedOnboarding: hasCompletedOnboarding || false,
                    });

                    // Set token in API defaults
                    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

                    // Setup token refresh
                    setupTokenRefresh(get, set, expiresAt);

                } catch (error: any) {
                    set({
                        error: error.response?.data?.error || 'Login failed',
                        isLoading: false,
                    });
                    throw error;
                }
            },

            register: async (email: string, password: string, name: string) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await api.post('/auth/register', { email, password, name });
                    const { user, accessToken, refreshToken, expiresIn } = response.data;

                    const expiresAt = Date.now() + (expiresIn * 1000);

                    set({
                        user,
                        accessToken,
                        refreshToken,
                        expiresAt,
                        isAuthenticated: true,
                        isLoading: false,
                    });

                    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                    setupTokenRefresh(get, set, expiresAt);

                } catch (error: any) {
                    set({
                        error: error.response?.data?.error || 'Registration failed',
                        isLoading: false,
                    });
                    throw error;
                }
            },

            refreshAccessToken: async () => {
                const { refreshToken } = get();
                if (!refreshToken) return false;

                try {
                    const response = await api.post('/auth/refresh', { refreshToken });
                    const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } = response.data;

                    const expiresAt = Date.now() + (expiresIn * 1000);

                    set({
                        accessToken: newAccessToken,
                        refreshToken: newRefreshToken,
                        expiresAt,
                    });

                    api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                    setupTokenRefresh(get, set, expiresAt);

                    return true;
                } catch {
                    // Refresh failed, logout
                    get().logout();
                    return false;
                }
            },

            logout: () => {
                const { refreshToken } = get();
                api.post('/auth/logout', { refreshToken }).catch(() => { });
                delete api.defaults.headers.common['Authorization'];

                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    expiresAt: null,
                    isAuthenticated: false,
                });
            },

            clearError: () => set({ error: null }),

            setOnboardingComplete: () => set({ hasCompletedOnboarding: true }),

            skipOnboarding: () => set({ hasCompletedOnboarding: true }),

            setAuth: (data: { user: User; accessToken: string; refreshToken: string; expiresIn: number; isNewUser?: boolean }) => {
                const expiresAt = Date.now() + (data.expiresIn * 1000);

                set({
                    user: data.user,
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    expiresAt,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                    // New users need onboarding, returning users don't
                    hasCompletedOnboarding: data.isNewUser === false,
                });

                api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
                setupTokenRefresh(get, set, expiresAt);
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                expiresAt: state.expiresAt,
                isAuthenticated: state.isAuthenticated,
                hasCompletedOnboarding: state.hasCompletedOnboarding,
            }),
        }
    )
);

// Helper to setup automatic token refresh
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

function setupTokenRefresh(
    get: () => AuthState,
    _set: (partial: Partial<AuthState>) => void,
    expiresAt: number
) {
    if (refreshTimeout) clearTimeout(refreshTimeout);

    // Refresh 1 minute before expiration
    const refreshIn = expiresAt - Date.now() - 60000;

    if (refreshIn > 0) {
        refreshTimeout = setTimeout(() => {
            get().refreshAccessToken();
        }, refreshIn);
    }
}

// Initialize auth header on app load
const { accessToken, expiresAt, refreshAccessToken } = useAuthStore.getState();
if (accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    // Check if token needs refresh on load
    if (expiresAt && Date.now() > expiresAt - 60000) {
        refreshAccessToken();
    }
}
